import { Form, Input } from 'antd'
import { useTranslation } from 'react-i18next'

import { useRejectCancellation } from './useRejectCancellation'
import { Popup, confirmPopup } from '@/components/popup'

interface RejectCancellationModalProps {
  cancellationId: string
  open: boolean
  onClose: () => void
}

interface RejectFormValues {
  rejectionReason: string
}

export function RejectCancellationModal({
  cancellationId,
  open,
  onClose,
}: RejectCancellationModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<RejectFormValues>()
  const { rejectCancellation, isRejecting } = useRejectCancellation(() => {
    form.resetFields()
    onClose()
  })

  const handleOk = () => {
    void form.validateFields().then((values) => {
      confirmPopup({
        title: t('common.modals.rejectCancellationTitle'),
        content: t('common.modals.rejectCancellationMessage'),
        okText: t('common.buttons.confirm'),
        cancelText: t('common.buttons.cancel'),
        okButtonProps: { 'data-testid': 'reject-cancellation-confirm-ok-btn' },
        cancelButtonProps: { 'data-testid': 'reject-cancellation-confirm-cancel-btn' },
        onOk: () => {
          rejectCancellation(cancellationId, values.rejectionReason)
        },
      })
    })
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Popup
      data-testid="reject-cancellation-modal"
      title={t('common.modals.rejectCancellationTitle')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={isRejecting}
      okText={t('common.buttons.reject')}
      cancelText={t('common.buttons.cancel')}
      okButtonProps={{ 'data-testid': 'reject-cancellation-modal-ok-btn' }}
      cancelButtonProps={{ 'data-testid': 'reject-cancellation-modal-cancel-btn' }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="rejectionReason"
          label={t('pages.cancellations.fields.rejectionReason')}
          rules={[{ required: true, whitespace: true }]}
        >
          <Input.TextArea
            data-testid="reject-cancellation-reason-input"
            rows={3}
            placeholder={t('pages.cancellations.rejectionReasonPlaceholder')}
          />
        </Form.Item>
      </Form>
    </Popup>
  )
}
